import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppBar, Box, Button, Container, Paper, Toolbar, Typography } from '@mui/material';
import { Me, PortalApi } from './portalApi';
import { clearPortalToken, getPortalToken, portalErrorMessage, setPortalUnauthorizedHandler } from './portalHttp';
import { PortalSessionContext } from './PortalSession';
import PortalLogin from './PortalLogin';
import AcceptInvite, { ResetPassword } from './AcceptInvite';
import ForgotPassword from './ForgotPassword';
import PortalInvoices from './PortalInvoices';
import PortalInvoiceDetail from './PortalInvoiceDetail';
import PortalUploads from './PortalUploads';
import { PortalCreditNotes, PortalReceipts, PortalStatement } from './PortalDocuments';
import SupplierPortalShell from './supplier/SupplierPortalShell';
import { getPortalParty } from './supplier/supplierPortalApi';

// ---- P1-B customer portal: own routes and own minimal layout (no staff header, no staff auth) ----
const NotFound: React.FC = () => (
  <Container maxWidth="sm" sx={{ py: 8 }}><Typography variant="h5">Not found</Typography></Container>
);

/** Login-side pages: card with the supplier's name (404 when the portal is off or the code is unknown). */
const PublicShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { code = '' } = useParams<{ code: string }>();
  const [name, setName] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    PortalApi.institutionName(code)
      .then(r => { if (!cancelled) setName(r.name); })
      .catch(e => { if (!cancelled) setMissing(e?.response?.status === 404 || e?.response?.status === 503); });
    return () => { cancelled = true; };
  }, [code]);
  if (missing) return <NotFound />;
  return (
    <Container maxWidth="xs" sx={{ py: 6 }}>
      <Typography variant="h5" align="center" gutterBottom>{name ?? ' '}</Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>Customer and supplier portal</Typography>
      <Paper sx={{ p: 3 }}>{children}</Paper>
    </Container>
  );
};

const NAV: Array<[string, string]> = [
  ['', 'Invoices'], ['statement', 'Statement'], ['receipts', 'Receipts'], ['credit-notes', 'Credit notes'], ['uploads', 'Uploads'],
];

/** Everything behind the login: loads the contact, redirects to login without a session. */
const PortalAuthed: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const hasToken = !!getPortalToken();
  const isSupplier = getPortalParty() === 'SUPPLIER'; // F8: a supplier session never loads the customer area
  const [me, setMe] = useState<Me | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasToken || isSupplier) return;
    let cancelled = false;
    PortalApi.me()
      .then(m => { if (!cancelled) setMe(m); })
      .catch(e => { if (!cancelled) setLoadError(portalErrorMessage(e, 'Could not load your account')); });
    return () => { cancelled = true; };
  }, [hasToken, isSupplier, code]);

  if (!hasToken) return <Navigate to={`/portal/${encodeURIComponent(code)}/login`} replace />;
  if (isSupplier) return <Navigate to={`/portal/${encodeURIComponent(code)}/supplier`} replace />;

  const base = `/portal/${encodeURIComponent(code)}`;
  const logout = async () => {
    try { await PortalApi.logout(); } catch { /* the session may already be gone */ }
    clearPortalToken();
    navigate(`${base}/login`, { replace: true });
  };

  return (
    <PortalSessionContext.Provider value={{ me, code }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 180 }}>
            <Typography variant="subtitle1" fontWeight="bold">{me?.institutionName ?? ' '}</Typography>
            <Typography variant="caption" color="text.secondary">{me ? `${me.contactName} - ${me.customerName}` : ' '}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {NAV.map(([path, label]) => {
              const to = path ? `${base}/${path}` : base;
              const active = path ? location.pathname.startsWith(to) : location.pathname === base || location.pathname.startsWith(`${base}/invoices`);
              return <Button key={label} component={RouterLink} to={to} size="small" variant={active ? 'contained' : 'text'}>{label}</Button>;
            })}
            <Button size="small" color="inherit" onClick={logout}>Logout</Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {loadError ? <Typography color="error">{loadError}</Typography> : <Outlet />}
      </Container>
    </PortalSessionContext.Provider>
  );
};

const PortalRoot: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setPortalUnauthorizedHandler(code => navigate(code ? `/portal/${encodeURIComponent(code)}/login` : '/portal/', { replace: true }));
    return () => setPortalUnauthorizedHandler(null);
  }, [navigate]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Routes>
        <Route path=":code/login" element={<PublicShell><PortalLogin /></PublicShell>} />
        <Route path=":code/accept" element={<PublicShell><AcceptInvite /></PublicShell>} />
        <Route path=":code/reset" element={<PublicShell><ResetPassword /></PublicShell>} />
        <Route path=":code/forgot" element={<PublicShell><ForgotPassword /></PublicShell>} />
        <Route path=":code/supplier/*" element={<SupplierPortalShell />} />
        <Route path=":code" element={<PortalAuthed />}>
          <Route index element={<PortalInvoices />} />
          <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
          <Route path="statement" element={<PortalStatement />} />
          <Route path="receipts" element={<PortalReceipts />} />
          <Route path="credit-notes" element={<PortalCreditNotes />} />
          <Route path="uploads" element={<PortalUploads />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Box>
  );
};

export default PortalRoot;
// ---- end P1-B ----
